import { cmsNew } from "@/lib/cms/cms-new-i18n";

import { LAYOUT_GAP_PX, type GapInlineStyle, type LayoutGap } from "./types";
import { cx } from "./internal";

export function getLayoutGapOptions(): ReadonlyArray<{
  label: string;
  value: LayoutGap;
}> {
  return [
    { label: cmsNew("cms.new.options.gap.none", "Aucun"), value: "none" },
    { label: cmsNew("cms.new.options.gap.sm", "Petit"), value: "sm" },
    { label: cmsNew("cms.new.options.gap.md", "Moyen"), value: "md" },
    { label: cmsNew("cms.new.options.gap.lg", "Grand"), value: "lg" },
    { label: cmsNew("cms.new.options.gap.xl", "Très grand"), value: "xl" },
  ];
}


/** Espacements (gap ET padding) → classes complètes (JIT-safe). */
const GAP_CLASS: Record<LayoutGap, string> = {
  none: "gap-0",
  sm: "gap-2",
  md: "gap-4",
  lg: "gap-8",
  xl: "gap-12",
};

const GAP_ROW_CLASS: Record<LayoutGap, string> = {
  none: "gap-x-0",
  sm: "gap-x-2",
  md: "gap-x-4",
  lg: "gap-x-8",
  xl: "gap-x-12",
};

const GAP_COL_CLASS: Record<LayoutGap, string> = {
  none: "gap-y-0",
  sm: "gap-y-2",
  md: "gap-y-4",
  lg: "gap-y-8",
  xl: "gap-y-12",
};

/** Gap uniforme ou horizontal/vertical séparés (rétro-compat : `gap` seul). */
export function resolveGapClasses(props: {
  gap: LayoutGap;
  gapRow?: LayoutGap | null;
  gapCol?: LayoutGap | null;
}): string {
  const row = props.gapRow ?? props.gap;
  const col = props.gapCol ?? props.gap;
  if (row === col) return GAP_CLASS[row];
  return cx(GAP_ROW_CLASS[row], GAP_COL_CLASS[col]);
}

/** Classes Tailwind et/ou style inline quand une valeur px custom est définie. */
export function resolveGapLayout(props: {
  gap: LayoutGap;
  gapCustomPx?: number | null;
  gapRow?: LayoutGap | null;
  gapCol?: LayoutGap | null;
  gapRowCustomPx?: number | null;
  gapColCustomPx?: number | null;
}): { className: string; style: GapInlineStyle } {
  const rowCustom =
    props.gapRowCustomPx != null && Number.isFinite(props.gapRowCustomPx)
      ? Math.max(0, Math.round(props.gapRowCustomPx))
      : null;
  const colCustom =
    props.gapColCustomPx != null && Number.isFinite(props.gapColCustomPx)
      ? Math.max(0, Math.round(props.gapColCustomPx))
      : null;
  const uniformCustom =
    props.gapCustomPx != null && Number.isFinite(props.gapCustomPx)
      ? Math.max(0, Math.round(props.gapCustomPx))
      : null;

  if (rowCustom != null || colCustom != null) {
    const row =
      rowCustom ??
      LAYOUT_GAP_PX[props.gapRow ?? props.gap] ??
      LAYOUT_GAP_PX[props.gap];
    const col =
      colCustom ??
      LAYOUT_GAP_PX[props.gapCol ?? props.gap] ??
      LAYOUT_GAP_PX[props.gap];
    return {
      className: "",
      style: { columnGap: `${row}px`, rowGap: `${col}px` },
    };
  }
  if (uniformCustom != null) {
    return { className: "", style: { gap: `${uniformCustom}px` } };
  }
  return {
    className: resolveGapClasses(props),
    style: {},
  };
}

/**
 * Écarts effectifs en px (horizontal = colonnes, vertical = lignes), mêmes
 * priorités que `resolveGapLayout` : px par axe > px uniforme > préréglages.
 */
export function resolveGapPx(props: {
  gap: LayoutGap;
  gapCustomPx?: number | null;
  gapRow?: LayoutGap | null;
  gapCol?: LayoutGap | null;
  gapRowCustomPx?: number | null;
  gapColCustomPx?: number | null;
}): { columnGap: number; rowGap: number } {
  const px = (v: number | null | undefined) =>
    v != null && Number.isFinite(v) ? Math.max(0, Math.round(v)) : null;
  const rowCustom = px(props.gapRowCustomPx);
  const colCustom = px(props.gapColCustomPx);
  const uniformCustom = px(props.gapCustomPx);
  if (rowCustom == null && colCustom == null && uniformCustom != null) {
    return { columnGap: uniformCustom, rowGap: uniformCustom };
  }
  const preset = (g: LayoutGap | null | undefined) =>
    LAYOUT_GAP_PX[g ?? props.gap] ?? LAYOUT_GAP_PX[props.gap] ?? 0;
  return {
    columnGap: rowCustom ?? preset(props.gapRow),
    rowGap: colCustom ?? preset(props.gapCol),
  };
}

/** Style inline gap du Container (valeur px custom). */
export function containerGapStyle(props: {
  gap: LayoutGap;
  gapCustomPx?: number | null;
}): GapInlineStyle {
  return resolveGapLayout(props).style;
}
